import { useCallback } from 'react';
import { useAlert } from 'react-alert';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useActions } from '../../../../hooks/useActions';
import { useApiResponseFail } from '../../../../hooks/useApiResponseFail';
import { useProcessing } from '../../../../hooks/useProcessing';
import {
  ProductSubscriptionOverviewPageParams,
  RoutePath,
} from '../../../../models/enums/RoutePath';
import { IValidationErrorResponse } from '../../../../models/ValidationErrorResponse';
import { IWarningRatification } from '../../../../models/interfaces/WarningRatification';
import { organizationContractsSelector } from '../../../../store/redux-slices/contracts/selectors';
import { updateModalState } from '../../../../store/redux-slices/modals';
import {
  organizationNameSelector,
  organizationSelector,
} from '../../../../store/redux-slices/organizations/selectors';
import {
  SUBSCRIPTION_EDITING_PROCESSING,
  SUBSCRIPTION_RENEWAL_SUGGESTION_LOADING_PROCESSING,
} from '../../../../store/redux-slices/processes/constants';
import { ProductSubscriptionRenewPageParamsType } from '../../../../models/types/RoutePath';
import { useNavigateSearch } from '../../../../hooks/useNavigateSearch';
import { SUBSCRIPTION_RENEW_ANALYSE_MODAL } from '../../../../styledComponents/Modals/SubscriptionAnalyseModal/subscriptionAnalyse.consts';
import { ERRORS_FOR_SHOW_ANALYSE_MODAL } from './docSubscriptionRenewPageForm.const';
import { AddContractModalFormDetails } from '../../../../styledComponents/Modals/AddContractModal/AddContractModalForm/addContractModalForm.models';
import { ADD_CONTRACT_MODAL_NAME } from '../../../../styledComponents/Modals/AddContractModal/addContractModal.const';
import { editingDOCSubscriptionDataSelector } from '../../../../store/redux-slices/subscriptions/dutyOfCare/selectors';
import { updateProductDOCSubscription } from '../../../../store/saga-slices/subscriptions/dutyOfCare';
import { IEditDOCOrganizationSubscriptionModel } from '../../../../models/interfaces/Subscription/DutyOfCare/EditDOCOrganizationSubscriptionModel';
import { IUpdateDOCSubscriptionPayload } from '../../../../store/saga-slices/subscriptions/dutyOfCare/models';
import { useFormSubmitMessage } from '../../../../hooks/useFormSubmitMessage';
import { needShowAnalyseModal } from '../../../../styledComponents/Modals/SubscriptionAnalyseModal/subscriptionAnalyse.utils';

export const useDOCEditSubscriptionRenewForm = () => {
  const navigate = useNavigateSearch();

  const { organizationId, subscriptionId, productType } =
    useParams<ProductSubscriptionRenewPageParamsType>();

  const alert = useAlert();

  const {
    prepareSubscriptionSubmitFailMessage,
    prepareSubscriptionSubmitSuccessMessage,
  } = useFormSubmitMessage();

  const UpdateModalState = useActions(updateModalState);

  const UpdateProductSubscription = useActions(updateProductDOCSubscription);
  const subscriptionData = useSelector(editingDOCSubscriptionDataSelector);

  const isSubscriptionUpdating = useProcessing(SUBSCRIPTION_EDITING_PROCESSING);

  const isSuggestionDataLoading = useProcessing(
    SUBSCRIPTION_RENEWAL_SUGGESTION_LOADING_PROCESSING,
  );

  const organizationName = useSelector(organizationNameSelector);
  const { handleResponse } = useApiResponseFail();

  const organization = useSelector(organizationSelector);
  const organizationContracts = useSelector(organizationContractsSelector);

  const handleSubmit = (values: IEditDOCOrganizationSubscriptionModel) => {
    updateOrganizationSubscription(values);
  };

  const updateOrganizationSubscription = useCallback(
    (
      values: IEditDOCOrganizationSubscriptionModel,
      ratifications: IWarningRatification[] = [],
    ) => {
      const extendedValues: IEditDOCOrganizationSubscriptionModel = {
        ...values,
        ratifications,
      };
      const createNewSubscriptionPayload: IUpdateDOCSubscriptionPayload = {
        organizationId,
        subscriptionId,
        data: extendedValues,
        error: (err: IValidationErrorResponse) => {
          if (needShowAnalyseModal(err, ERRORS_FOR_SHOW_ANALYSE_MODAL)) {
            UpdateModalState(SUBSCRIPTION_RENEW_ANALYSE_MODAL);
            return;
          }

          handleResponse({
            err,
            values,
            ratifications,
            alertErrorMessage: prepareSubscriptionSubmitFailMessage(
              organizationName,
              values?.isDraft,
            ),
          });
        },
        success: () => {
          navigate(
            RoutePath.ClientOverviewProductSubscriptionOverviewPageFullPath,
            {
              params: {
                [ProductSubscriptionOverviewPageParams.OrganizationId]:
                  organizationId,
                [ProductSubscriptionOverviewPageParams.SubscriptionId]:
                  subscriptionId,
                [ProductSubscriptionOverviewPageParams.SubscriptionType]:
                  productType,
              },
            },
          );
          alert.success(
            prepareSubscriptionSubmitSuccessMessage(
              organizationName,
              values?.isDraft,
            ),
          );
        },
      };
      UpdateProductSubscription(createNewSubscriptionPayload);
    },
    [
      UpdateModalState,
      UpdateProductSubscription,
      alert,
      handleResponse,
      navigate,
      organizationId,
      organizationName,
      prepareSubscriptionSubmitFailMessage,
      prepareSubscriptionSubmitSuccessMessage,
      productType,
      subscriptionId,
    ],
  );

  const onAddNewContractHandler = () => {
    const details: AddContractModalFormDetails = {
      organizationId,
    };
    UpdateModalState([ADD_CONTRACT_MODAL_NAME, details]);
  };

  const initState = {
    transferAllEligibleMemberships: true,
    packageOptions: {
      firmWide: false,
    },
    ...subscriptionData,
  };

  return {
    handleSubmit,
    organization,
    canChangeContract: subscriptionData?.metadata?.canChangeContract,
    isSubscriptionUpdating,
    organizationContracts,
    isSuggestionDataLoading,
    initState,
    UpdateModalState,
    updateOrganizationSubscription,
    productType,
    onAddNewContractHandler,
  };
};
